import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Grid,
  Box,
  Typography,
  Pagination,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Fade
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useTheme } from '@mui/material/styles';
import PokemonCard from '../components/pokemon/PokemonCard';
import PokemonFilters from '../components/pokemon/PokemonFilters';
import usePokemonList from '../hooks/usePokemonList';
import useVgcFormats from '../hooks/useVgcFormats';
import useFormatPokemon from '../hooks/useFormatPokemon';
import { matchesTypeFilter } from '../utils/pokemonTypes';
import SEO from '../components/SEO';

const DEFAULT_STAT_FILTERS = { hp: 0, attack: 0, defense: 0, 'special-attack': 0, 'special-defense': 0, speed: 0, total: 0 };

const PokemonListPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Get URL parameters
  const searchParams = new URLSearchParams(location.search);
  const pageParam = parseInt(searchParams.get('page')) || 1;
  const searchParam = searchParams.get('search') || '';
  const typesParam = searchParams.get('types') || '';
  const filterModeParam = searchParams.get('filterMode') || 'OR';
  const formatParam = searchParams.get('format') || null;

  const [currentPage, setCurrentPage] = useState(pageParam);
  const [searchQuery, setSearchQuery] = useState(searchParam);
  const [selectedTypes, setSelectedTypes] = useState(
    typesParam ? typesParam.split(',').filter(Boolean) : []
  );
  const [filterMode, setFilterMode] = useState(filterModeParam);
  const [selectedFormat, setSelectedFormat] = useState(formatParam);
  const [statFilters, setStatFilters] = useState(DEFAULT_STAT_FILTERS);
  const [typeFilterExpanded, setTypeFilterExpanded] = useState(selectedTypes.length > 0);

  // Format data
  const { formats: vgcFormats, loading: formatsLoading } = useVgcFormats();
  const { pokemonNames: formatPokemonNames, loading: formatPokemonLoading } = useFormatPokemon(selectedFormat);
  
  const itemsPerPage = 24;
  
  // Fetch Pokémon list (ahora mucho más rápido)
  const { pokemonList, loading, error, cacheInfo } = usePokemonList({
    limit: 1025,
    types: selectedTypes,
    includeStats: true
  });

  // Log cuando cambie la lista de Pokémon
  useEffect(() => {
    console.log('📊 PokemonListPage: pokemonList updated:', {
      count: pokemonList.length,
      firstPokemon: pokemonList[0],
      hasTypes: pokemonList.filter(p => p.types && p.types.length > 0).length
    });
  }, [pokemonList]);

  // Log info de caché (opcional, para debugging)
  useEffect(() => {
    if (cacheInfo) {
      console.log('📊 Cache info:', {
        count: cacheInfo.count,
        ageMinutes: cacheInfo.ageMinutes,
        needsUpdate: cacheInfo.needsUpdate
      });
    }
  }, [cacheInfo]);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();

    if (currentPage > 1) params.set('page', currentPage.toString());
    if (searchQuery) params.set('search', searchQuery);
    if (selectedTypes.length > 0) params.set('types', selectedTypes.join(','));
    if (filterMode !== 'OR') params.set('filterMode', filterMode);
    if (selectedFormat) params.set('format', selectedFormat);

    const newSearch = params.toString();
    const currentSearch = location.search.slice(1);

    if (newSearch !== currentSearch) {
      navigate(`${location.pathname}?${newSearch}`, { replace: true });
    }
  }, [currentPage, searchQuery, selectedTypes, filterMode, selectedFormat, navigate, location.pathname, location.search]);

  // Filter and search Pokémon
  const filteredPokemon = useMemo(() => {
    let filtered = [...pokemonList];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(pokemon =>
        pokemon.displayName.toLowerCase().includes(query) ||
        pokemon.id.toString().includes(query)
      );
    }

    // Apply type filter
    if (selectedTypes.length > 0) {
      filtered = filtered.filter(pokemon =>
        matchesTypeFilter(pokemon, selectedTypes, filterMode)
      );
    }

    // Apply format filter (match against Smogon stats Pokemon names)
    if (formatPokemonNames !== null && !formatPokemonLoading) {
      filtered = filtered.filter(pokemon => {
        const name = (pokemon.name || '').toLowerCase();
        if (formatPokemonNames.has(name)) return true;
        // Fallback: check if any format name is a substring (handles PokeAPI suffix variants)
        for (const fmtName of formatPokemonNames) {
          if (name.startsWith(fmtName) || fmtName.startsWith(name + '-')) return true;
        }
        return false;
      });
    }

    // Apply stat filters
    const hasStatFilter = Object.values(statFilters).some(v => v > 0);
    if (hasStatFilter) {
      filtered = filtered.filter(pokemon => {
        if (!pokemon.stats) return false;
        const statsMap = Object.fromEntries(pokemon.stats.map(s => [s.name, s.baseStat]));
        const total = pokemon.stats.reduce((sum, s) => sum + s.baseStat, 0);
        for (const [key, minVal] of Object.entries(statFilters)) {
          if (minVal === 0) continue;
          const actual = key === 'total' ? total : (statsMap[key] ?? 0);
          if (actual < minVal) return false;
        }
        return true;
      });
    }

    return filtered;
  }, [pokemonList, searchQuery, selectedTypes, filterMode, formatPokemonNames, formatPokemonLoading, statFilters]);

  // Pagination
  const totalPages = Math.ceil(filteredPokemon.length / itemsPerPage);
  const paginatedPokemon = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredPokemon.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredPokemon, currentPage, itemsPerPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedTypes, filterMode, selectedFormat, statFilters]);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handlePokemonClick = (pokemon) => {
    if (!pokemon) {
      console.error('handlePokemonClick called with undefined pokemon');
      return;
    }

    // Prioridad: ID > name
    const identifier = pokemon.id || pokemon.name;
    
    if (!identifier) {
      console.error('Pokemon has no valid identifier:', pokemon);
      return;
    }

    console.log('Navigating to pokemon:', identifier, pokemon);
    navigate(`/pokemon/${identifier}`);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const handleTypesChange = (newTypes) => {
    setSelectedTypes(newTypes);
  };

  const handleFilterModeChange = (newMode) => {
    setFilterMode(newMode);
  };

  const handleFormatChange = (format) => {
    setSelectedFormat(format);
  };

  const handleStatFiltersChange = (newStatFilters) => {
    setStatFilters(newStatFilters);
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <SEO 
          title="Pokémon List"
          description="Browse and explore all Pokémon from Generations 1-9. Filter by type, search by name, and discover detailed stats for competitive VGC battles."
          keywords="pokemon list, pokemon database, pokemon types, pokemon generations, vgc pokemon"
        />
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '60vh' 
        }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl">
        <SEO 
          title="Pokémon List"
          description="Browse and explore all Pokémon from Generations 1-9. Filter by type, search by name, and discover detailed stats for competitive VGC battles."
          keywords="pokemon list, pokemon database, pokemon types, pokemon generations, vgc pokemon"
        />
        <Box sx={{ py: 4 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </Container>
    );
  }

  // Build dynamic SEO based on filters
  const buildSEOTitle = () => {
    if (searchQuery && selectedTypes.length > 0) {
      return `${searchQuery} - ${selectedTypes.join(', ')} Type Pokémon`;
    } else if (searchQuery) {
      return `Search: ${searchQuery} - Pokémon List`;
    } else if (selectedTypes.length > 0) {
      return `${selectedTypes.join(', ')} Type Pokémon`;
    }
    return 'All Pokémon - Complete Database';
  };

  const buildSEODescription = () => {
    if (searchQuery && selectedTypes.length > 0) {
      return `Find ${searchQuery} and other ${selectedTypes.join(', ')} type Pokémon. Showing ${filteredPokemon.length} results from our complete VGC Pokémon database.`;
    } else if (searchQuery) {
      return `Search results for "${searchQuery}". Browse ${filteredPokemon.length} Pokémon matching your search in our competitive VGC database.`;
    } else if (selectedTypes.length > 0) {
      return `Explore ${filteredPokemon.length} ${selectedTypes.join(', ')} type Pokémon. Complete stats, abilities, and movesets for competitive VGC battles.`;
    }
    return `Browse and explore all ${pokemonList.length} Pokémon from Generations 1-9. Filter by type, search by name, and discover detailed stats for competitive VGC battles.`;
  };

  return (
    <Container maxWidth="xl">
      <SEO 
        title={buildSEOTitle()}
        description={buildSEODescription()}
        keywords={`pokemon list, ${selectedTypes.join(', ')} type, ${searchQuery}, pokemon database, vgc pokemon, competitive pokemon`}
      />
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="h3" 
            sx={{ 
              color: 'white', 
              fontWeight: 'bold',
              mb: 1
            }}
          >
            Pokémon List
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            Browse and explore all Pokémon from Generations 1-9
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: 'rgba(255, 255, 255, 0.65)', mt: 1, maxWidth: '800px' }}
          >
            Search the complete Pokémon list and database for competitive VGC: find Pokémon by name or ID,
            filter by type, and view stats, abilities, common movesets and usage in the VGC metagame.
            Use this Pokémon database to build teams and research matchups.
          </Typography>
        </Box>

        {/* Search Bar */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search by name or ID..."
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClearSearch}
                    edge="end"
                    sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                backgroundColor: 'rgba(30, 30, 30, 0.9)',
                color: 'white',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.2)'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main
                }
              }
            }}
          />
        </Box>

        {/* Filters */}
        <PokemonFilters
          selectedFormat={selectedFormat}
          onFormatChange={handleFormatChange}
          formats={vgcFormats}
          formatsLoading={formatsLoading}
          selectedTypes={selectedTypes}
          onTypesChange={handleTypesChange}
          filterMode={filterMode}
          onFilterModeChange={handleFilterModeChange}
          statFilters={statFilters}
          onStatFiltersChange={handleStatFiltersChange}
          filteredCount={filteredPokemon.length}
          totalCount={pokemonList.length}
          expanded={typeFilterExpanded}
          onExpandedChange={setTypeFilterExpanded}
        />

        {/* Results Summary */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            {formatPokemonLoading ? (
              <span style={{ color: 'rgba(255,255,255,0.5)' }}>Loading format data...</span>
            ) : (
              <>
                Showing {paginatedPokemon.length} of {filteredPokemon.length} Pokémon
                {(searchQuery || selectedTypes.length > 0 || selectedFormat) && (
                  <span style={{ color: theme.palette.primary.main, marginLeft: '8px' }}>
                    (filtered from {pokemonList.length} total)
                  </span>
                )}
              </>
            )}
          </Typography>

          {totalPages > 1 && (
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Page {currentPage} of {totalPages}
            </Typography>
          )}
        </Box>

        {/* No results message */}
        {filteredPokemon.length === 0 ? (
          <Fade in={true}>
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                px: 2
              }}
            >
              <Typography variant="h5" sx={{ color: 'white', mb: 2 }}>
                No Pokémon found
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Try adjusting your search or filter criteria
              </Typography>
            </Box>
          </Fade>
        ) : (
          <>
            {/* Pokémon Grid */}
            <Grid container spacing={3}>
              {paginatedPokemon.map((pokemon) => {
                // VALIDACIÓN: Asegurar que cada pokemon tiene un identificador único
                const key = pokemon.id || pokemon.name || `pokemon-${Math.random()}`;
                
                return (
                  <Grid item xs={6} sm={4} md={3} lg={2} key={key}>
                    <Fade in={true} timeout={300}>
                      <Box>
                        <PokemonCard
                          pokemon={pokemon}
                          onClick={() => handlePokemonClick(pokemon)}
                        />
                      </Box>
                    </Fade>
                  </Grid>
                );
              })}
            </Grid>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                  sx={{
                    '& .MuiPaginationItem-root': {
                      color: 'white',
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                      '&:hover': {
                        backgroundColor: 'rgba(36, 204, 159, 0.1)',
                        borderColor: theme.palette.primary.main
                      },
                      '&.Mui-selected': {
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                        fontWeight: 'bold',
                        '&:hover': {
                          backgroundColor: theme.palette.primary.dark
                        }
                      }
                    }
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Box>
    </Container>
  );
};

export default PokemonListPage;